export default function Services() {
  const services = [
    {
      icon: 'ri-home-smile-line',
      title: 'Property Dealing',
      description: 'Expert guidance in buying, selling, and leasing residential and commercial properties with comprehensive market analysis and negotiation support.',
      features: ['Residential Plots', 'Commercial Plots', 'Homes & Villas', 'Verified Inventory in DHA, Multan & Other Societies']
    },
    {
      icon: 'ri-building-line',
      title: 'Construction & Development',
      description: 'End-to-end construction management from planning to execution, ensuring quality, timely delivery, and adherence to all safety standards.',
      features: ['Grey Structure', 'Full Finishing', 'Architecture & Design', 'Renovation Services']
    },
    {
      icon: 'ri-landscape-line',
      title: 'Land Development',
      description: 'Strategic land acquisition and development services, transforming raw land into valuable residential or commercial properties.',
      features: ['Site Planning', 'Infrastructure', 'Zoning Approval']
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Investment Consulting',
      description: 'Professional investment advice to maximize returns on real estate investments with detailed market research and risk assessment.',
      features: ['Short-term & Long-term Investment Planning', 'High-return Real Estate Opportunities', 'Safe, Legally Verified Investments', 'ROI Planning & Profit Calculations']
    },
    {
      icon: 'ri-tools-line',
      title: 'Renovation & Remodeling',
      description: 'Transform existing properties with our expert renovation services, enhancing value and functionality while maintaining structural integrity.',
      features: ['Design Planning', 'Modern Upgrades', 'Value Addition']
    },
    {
      icon: 'ri-file-text-line',
      title: 'Legal Documentation',
      description: 'Complete assistance with property documentation, title verification, and legal compliance to ensure smooth and secure transactions.',
      features: ['Title Verification', 'Contract Review', 'Compliance Check']
    }
  ];

  return (
    <section id="services" className="py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 mb-6 bg-blue-500/10 border border-blue-500/20 px-5 py-2 rounded-full">
            <div className="w-2 h-2 bg-blue-600 rounded-full" />
            <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase">Our Services</span>
          </div>
          
          <h3 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            What We Offer{' '}
            <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
              in Real Estate
            </span>
          </h3>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From property dealing to construction management, we offer a complete suite of services designed to meet all your real estate needs with excellence and professionalism.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="group relative bg-white rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-blue-500/30 cursor-pointer overflow-hidden"
            >
              {/* Gradient Background on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-500 to-blue-600 rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>

                {/* Title */}
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h4>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-blue-500/10 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-blue-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Learn More Link */}
                <div className="flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-4 transition-all cursor-pointer">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>

              {/* Decorative Corner */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 group">
          <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-500 to-blue-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
            <i className="ri-file-shield-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">Documentation & Legal Assistance</h3>
          <p className="text-gray-600 leading-relaxed mb-6">
            Complete legal support and documentation services to ensure secure and compliant property transactions.
          </p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <i className="ri-check-line text-red-500"></i>
              <span>Sale Agreements</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <i className="ri-check-line text-red-500"></i>
              <span>Transfer Process</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <i className="ri-check-line text-red-500"></i>
              <span>Token & Bayana Management</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <i className="ri-check-line text-red-500"></i>
              <span>Safe Transaction Handling</span>
            </li>
          </ul>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-10 py-5 rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 font-bold text-lg whitespace-nowrap cursor-pointer"
            >
              Get a Free Consultation
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-10 py-5 rounded-xl hover:border-blue-500 hover:text-blue-600 transition-all duration-300 font-bold text-lg whitespace-nowrap cursor-pointer">
              View Our Portfolio
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
